import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import { alpha, useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { autorun } from "mobx";
import { useActivationController } from "./useActivationController";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import type { ActivationPreStepAction, ActivationProgressPayload } from "./types";

interface EditorActivationRunnerProps {
    runId?: string | null;
    onFinish?: () => void;
    onRunPreStepAction?: (action: ActivationPreStepAction) => void;
    onProgress?: (payload: ActivationProgressPayload) => void;
}

const ActivationTooltip: React.FC<any> = ({
    backProps,
    closeProps,
    index,
    primaryProps,
    size,
    skipProps,
    step,
    tooltipProps,
    isPlaying,
    onStopPlayback,
}) => {
    const data = (step as any)?.data ?? {};
    const title = String(data?.title || "").trim();
    const requiresInteraction = Boolean(data?.requiresInteraction);
    const showNextButton = !requiresInteraction;
    const progressLabel = `Step ${Number(index) + 1} of ${Number(size)}`;

    return (
        <div
            {...tooltipProps}
            style={{
                ...(tooltipProps?.style ?? {}),
                maxWidth: "448px",
                borderRadius: "20px",
                border: "1px solid rgba(148, 163, 184, 0.28)",
                boxShadow: "0px 24px 56px rgba(2, 6, 23, 0.45)",
                background: "#0F172A",
                padding: "30px",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "14px" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: "19px",
                            lineHeight: "25px",
                            fontWeight: 600,
                            color: "#F8FAFC",
                        }}
                    >
                        {title || "Guided step"}
                    </div>
                </div>
                <div
                    style={{
                        fontSize: "13px",
                        lineHeight: "18px",
                        fontWeight: 500,
                        color: "#94A3B8",
                        whiteSpace: "nowrap",
                    }}
                >
                    {progressLabel}
                </div>
                <button
                    {...closeProps}
                    style={{
                        border: "none",
                        background: "transparent",
                        color: "#94A3B8",
                        fontSize: "18px",
                        lineHeight: 1,
                        cursor: "pointer",
                        padding: 0,
                        marginLeft: "2px",
                    }}
                    aria-label="Close tour"
                >
                    x
                </button>
            </div>

            <div
                style={{
                    fontSize: "15px",
                    lineHeight: "23px",
                    color: "#CBD5E1",
                    marginBottom: "22px",
                }}
            >
                {step?.content}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                <button
                    {...skipProps}
                    style={{
                        border: "none",
                        background: "transparent",
                        color: "#94A3B8",
                        fontSize: "13px",
                        lineHeight: "19px",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                    }}
                >
                    Skip tour
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                        type="button"
                        onClick={() => {
                            if (!isPlaying) return;
                            onStopPlayback?.();
                        }}
                        disabled={!isPlaying}
                        style={{
                            border: "1px solid rgba(147, 197, 253, 0.7)",
                            borderRadius: "11px",
                            background: isPlaying ? "rgba(59, 130, 246, 0.24)" : "rgba(148, 163, 184, 0.12)",
                            color: isPlaying ? "#DBEAFE" : "#94A3B8",
                            fontSize: "13px",
                            lineHeight: "19px",
                            fontWeight: 600,
                            cursor: isPlaying ? "pointer" : "default",
                            padding: "8px 14px",
                        }}
                    >
                        Stop
                    </button>
                    {index > 0 && (
                        <button
                            {...backProps}
                            style={{
                                border: "none",
                                background: "transparent",
                                color: "#A5B4FC",
                                fontSize: "13px",
                                lineHeight: "19px",
                                fontWeight: 600,
                                cursor: "pointer",
                                padding: 0,
                            }}
                        >
                            Back
                        </button>
                    )}

                    {showNextButton ? (
                        <button
                            {...primaryProps}
                            style={{
                                border: "none",
                                borderRadius: "11px",
                                background: "#2563EB",
                                color: "#F8FAFC",
                                fontSize: "13px",
                                lineHeight: "19px",
                                fontWeight: 600,
                                cursor: "pointer",
                                padding: "8px 14px",
                            }}
                        >
                            Next
                        </button>
                    ) : (
                        <div
                            style={{
                                fontSize: "13px",
                                lineHeight: "19px",
                                fontWeight: 500,
                                color: "#93C5FD",
                            }}
                        >
                            Complete the highlighted action
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ActivationPendingTooltip: React.FC<{
    title: string;
    content: string;
    progressLabel: string;
}> = ({ title, content, progressLabel }) => {
    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 1499,
                padding: "24px",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "448px",
                    borderRadius: "20px",
                    border: "1px solid rgba(148, 163, 184, 0.28)",
                    boxShadow: "0px 24px 56px rgba(2, 6, 23, 0.45)",
                    background: "#0F172A",
                    padding: "30px",
                    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                }}
            >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "14px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: "19px",
                                lineHeight: "25px",
                                fontWeight: 600,
                                color: "#F8FAFC",
                            }}
                        >
                            {title}
                        </div>
                    </div>
                    <div
                        style={{
                            fontSize: "13px",
                            lineHeight: "18px",
                            fontWeight: 500,
                            color: "#94A3B8",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {progressLabel}
                    </div>
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                    }}
                >
                    <CircularProgress size={18} thickness={4.5} sx={{ color: "#60A5FA", flexShrink: 0 }} />
                    <div
                        style={{
                            fontSize: "15px",
                            lineHeight: "23px",
                            color: "#CBD5E1",
                        }}
                    >
                        {content}
                    </div>
                </div>
            </div>
        </div>
    );
};

const EditorActivationRunner: React.FC<EditorActivationRunnerProps> = ({
    runId,
    onFinish,
    onRunPreStepAction,
    onProgress,
}) => {
    const theme = useTheme();
    const [isPlaying, setIsPlaying] = useState<boolean>(CompositionStateStore.isPlaying === true);

    useEffect(() => {
        const dispose = autorun(() => {
            setIsPlaying(CompositionStateStore.isPlaying === true);
        });
        return () => {
            dispose();
        };
    }, []);

    const controller = useActivationController({
        runId,
        onFinish,
        onRunPreStepAction,
        onProgress,
    });

    if (!controller.hasActiveRun) return null;

    const pendingStep = controller.pendingStep;
    const pendingProgressLabel = `Step ${Number(controller.stepIndex) + 1} of ${Number(controller.steps.length)}`;

    return (
        <>
            {pendingStep ? (
                <ActivationPendingTooltip
                    title={String(pendingStep.pendingTitle || pendingStep.title || "Preparing step")}
                    content={String(pendingStep.pendingContent || "Getting this step ready.")}
                    progressLabel={pendingProgressLabel}
                />
            ) : null}
            <Joyride
                run={controller.shouldRunJoyride}
                stepIndex={controller.stepIndex}
                steps={controller.steps}
                callback={controller.onJoyrideCallback}
                tooltipComponent={(tooltipProps) => (
                    <ActivationTooltip
                        {...tooltipProps}
                        isPlaying={isPlaying}
                        onStopPlayback={async () => {
                            try {
                                await CompositionService.pause();
                            } catch (e) {
                                console.warn("[ActivationTour] Failed to stop playback:", e);
                            }
                        }}
                    />
                )}
                continuous
                showSkipButton
                hideBackButton={false}
                disableOverlayClose
                disableCloseOnEsc={false}
                spotlightClicks
                disableScrolling
                disableScrollParentFix
                scrollToFirstStep={false}
                locale={{
                    skip: "Skip tour",
                    back: "Back",
                    next: "Next",
                }}
                styles={{
                    options: {
                        zIndex: 1500,
                        arrowColor: "#0F172A",
                        backgroundColor: "#0F172A",
                        overlayColor:
                            theme.palette.mode === "dark"
                                ? alpha(theme.palette.common.black, 0.7)
                                : alpha(theme.palette.common.black, 0.62),
                        primaryColor: theme.palette.primary.main,
                        textColor: "#F8FAFC",
                        width: 396,
                    },
                    spotlight: {
                        borderRadius: "14px",
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.28)}, 0 0 0 12px ${alpha(
                            theme.palette.primary.main,
                            0.1
                        )}`,
                    },
                    tooltip: {
                        border: "1px solid rgba(148, 163, 184, 0.28)",
                        borderRadius: "18px",
                        boxShadow: "0px 24px 56px rgba(2, 6, 23, 0.45)",
                    },
                    tooltipContent: {
                        fontFamily: "Inter",
                        fontSize: "14px",
                        lineHeight: "21px",
                        color: "#CBD5E1",
                    },
                    buttonBack: {
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#A5B4FC",
                        textTransform: "none",
                    },
                    buttonNext: {
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#F8FAFC",
                        textTransform: "none",
                        borderRadius: "10px",
                        backgroundColor: "#2563EB",
                        padding: "7px 12px",
                    },
                    buttonClose: {
                        color: "#94A3B8",
                        fontSize: "16px",
                    },
                    buttonSkip: {
                        fontFamily: "Inter",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#94A3B8",
                        textTransform: "none",
                    },
                }}
            />
        </>
    );
};

export default EditorActivationRunner;
